import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import type { Quality } from "./save";

export type CamRig = "orbit" | "chase";

export function isMobileView(): boolean {
  return window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 900;
}

export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly clock = new THREE.Clock();
  private composer: EffectComposer;
  private bloom: UnrealBloomPass;
  private shake = 0;
  private orbit = new THREE.Vector3(0, 20, 20);
  private chase = new THREE.Vector3(0, 12.2, 14.5);
  private followX = 0;
  private lookX = 0;
  private rig: CamRig = "orbit";
  private quality: Quality = "high";

  constructor(canvas: HTMLCanvasElement) {
    const mobile = isMobileView();
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !mobile,
      alpha: false,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, mobile ? 1.4 : 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.setClearColor(0x05060a, 1);

    this.scene.fog = new THREE.FogExp2(0x05060a, 0.022);

    this.camera = new THREE.PerspectiveCamera(46, window.innerWidth / window.innerHeight, 0.1, 220);
    this.camera.position.copy(this.orbit);
    this.camera.lookAt(0, 0, -4);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      mobile ? 0.72 : 1.15,
      0.42,
      0.18,
    );
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());

    window.addEventListener("resize", () => this.resize());
  }

  setRig(rig: CamRig): void {
    this.rig = rig;
    if (rig === "orbit") {
      this.followX = 0;
      this.lookX = 0;
    }
  }

  setQuality(quality: Quality): void {
    this.quality = quality;
    const mobile = isMobileView();
    const cap = quality === "high" ? (mobile ? 1.4 : 2) : 1.05;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap));
    this.bloom.strength = quality === "high" ? (mobile ? 0.72 : 1.15) : 0.28;
    this.resize();
  }

  follow(x: number, dt: number): void {
    this.followX = THREE.MathUtils.damp(this.followX, x * 0.28, 4.5, dt);
    this.lookX = THREE.MathUtils.damp(this.lookX, x * 0.45, 5, dt);
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
    this.bloom.setSize(w, h);
  }

  addShake(amount: number): void {
    this.shake = Math.min(1.4, this.shake + amount);
  }

  updateCamera(time: number, playing: boolean): void {
    if (this.rig === "chase") {
      this.camera.fov = 52;
      this.camera.updateProjectionMatrix();
      const idle = playing ? 0.08 : 0.35;
      const bobY = Math.cos(time * 0.11) * 0.35 * idle;
      this.camera.position.set(this.chase.x + this.followX, this.chase.y + bobY, this.chase.z);
      if (this.shake > 0) {
        this.camera.position.x += (Math.random() - 0.5) * this.shake * 1.2;
        this.camera.position.y += (Math.random() - 0.5) * this.shake * 0.7;
        this.shake *= 0.84;
      }
      this.camera.lookAt(this.lookX, 0.2, playing ? -10 : -14);
      return;
    }

    this.camera.fov = 46;
    this.camera.updateProjectionMatrix();
    const idle = playing ? 0.15 : 1;
    const bobX = Math.sin(time * 0.18) * 3.2 * idle;
    const bobY = Math.cos(time * 0.11) * 0.8 * idle;
    this.camera.position.set(this.orbit.x + bobX, this.orbit.y + bobY, this.orbit.z);
    if (this.shake > 0) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake * 1.4;
      this.camera.position.y += (Math.random() - 0.5) * this.shake * 0.8;
      this.shake *= 0.84;
    }
    this.camera.lookAt(bobX * 0.12, 0, playing ? -4 : -10);
  }

  render(): void {
    if (this.quality === "low") this.renderer.render(this.scene, this.camera);
    else this.composer.render();
  }
}
